import React, { useState, useEffect } from 'react';
import { judgeAPI } from '../services/api';
import toast from 'react-hot-toast';

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, clat, cuet
  const [sortBy, setSortBy] = useState('score'); // score, date
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const response = await judgeAPI.getScoredArticles();
      setArticles(response.data?.data || []);
    } catch (error) {
      toast.error(`Failed to load articles: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles
    .filter(article => {
      if (filter === 'all') return true;
      return article.applicable_exams?.includes(filter.toUpperCase());
    })
    .sort((a, b) => {
      if (sortBy === 'score') {
        return (b.overall_score || 0) - (a.overall_score || 0);
      }
      return new Date(b.published_at || b.scored_at || 0) - new Date(a.published_at || a.scored_at || 0);
    });

  const getScoreColor = (score) => {
    if (score >= 85) return 'from-green-500 to-emerald-600';
    if (score >= 70) return 'from-yellow-500 to-orange-600';
    return 'from-red-500 to-pink-600';
  };

  const getScoreBadgeColor = (score) => {
    if (score >= 85) return 'bg-gradient-to-r from-green-500 to-emerald-600 text-white';
    if (score >= 70) return 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white';
    return 'bg-gradient-to-r from-red-500 to-pink-600 text-white';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 p-8 shadow-2xl">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">Scored Articles</h1>
                  <p className="text-blue-100 mt-1">Articles meeting exam relevance criteria (Score ≥70)</p>
                </div>
              </div>
              <div className="flex items-center space-x-4 mt-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                  <span className="text-2xl font-bold text-white">{filteredArticles.length}</span>
                  <span className="text-xs text-blue-100 ml-2">Total Articles</span>
                </div>
              </div>
            </div>
            <button
              onClick={loadArticles}
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white rounded-xl px-6 py-3 font-semibold transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh</span>
            </button>
          </div>
        </div>
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white opacity-10"></div>
        <div className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-white opacity-10"></div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm font-semibold text-gray-700 mb-2 block">📚 Filter by Exam</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm py-2.5 px-4 transition-all"
            >
              <option value="all">All Exams</option>
              <option value="clat">CLAT Only</option>
              <option value="cuet">CUET Only</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm font-semibold text-gray-700 mb-2 block">🎯 Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm py-2.5 px-4 transition-all"
            >
              <option value="score">Score (High to Low)</option>
              <option value="date">Date (Recent First)</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-indigo-600"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-indigo-100"></div>
            </div>
          </div>
        </div>
      ) : filteredArticles.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
          <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-full p-6 inline-block mb-4">
            <svg className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles found</h3>
          <p className="text-gray-500 mb-6">
            Start by triggering RSS ingestion or uploading an e-paper
          </p>
          <button
            onClick={() => window.location.href = '/rss-ingestion'}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transition-all"
          >
            Trigger RSS Ingestion
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {filteredArticles.map((article, idx) => (
            <div
              key={article._id || idx}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-indigo-200"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Title and Summary */}
                    <div className="mb-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                        {article.title || 'Untitled'}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {article.summary || article.content?.substring(0, 150)}...
                      </p>
                    </div>

                    {/* Metadata */}
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      {/* Source */}
                      <div className="flex items-center text-sm text-gray-500">
                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                        {article.source_name || 'Unknown'}
                      </div>

                      {/* Date */}
                      <div className="flex items-center text-sm text-gray-500">
                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {article.published_at
                          ? new Date(article.published_at).toLocaleDateString()
                          : (article.scored_at ? new Date(article.scored_at).toLocaleDateString() : '-')}
                      </div>

                      {/* Exam Tags */}
                      <div className="flex gap-2">
                        {article.applicable_exams?.map(exam => (
                          <span key={exam} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm">
                            {exam}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Score Badge */}
                  <div className="ml-4 flex-shrink-0">
                    <div className={`relative ${getScoreBadgeColor(article.overall_score)} rounded-2xl p-4 shadow-lg`}>
                      <div className="text-center">
                        <div className="text-3xl font-bold">{article.overall_score || 0}</div>
                        <div className="text-xs opacity-90">Score</div>
                      </div>
                      {article.overall_score >= 85 && (
                        <div className="absolute -top-2 -right-2 bg-yellow-400 rounded-full p-1">
                          <svg className="h-4 w-4 text-yellow-900" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setSelectedArticle(article)}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center group"
                  >
                    <svg className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Full Article
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Article View Modal */}
      {selectedArticle && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in" onClick={() => setSelectedArticle(null)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 px-6 py-5 flex items-start justify-between">
              <div className="flex-1 mr-4">
                <h3 className="text-xl font-bold text-white mb-2">{selectedArticle.title}</h3>
                <div className="flex flex-wrap items-center gap-3">
                  {selectedArticle.source_name && (
                    <span className="inline-flex items-center text-white/90 text-sm">
                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                      </svg>
                      {selectedArticle.source_name}
                    </span>
                  )}
                  {(selectedArticle.published_at || selectedArticle.scored_at) && (
                    <span className="inline-flex items-center text-white/90 text-sm">
                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {new Date(selectedArticle.published_at || selectedArticle.scored_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedArticle(null)}
                className="flex-shrink-0 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-xl p-2 transition-all"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {/* Score and Exams */}
              <div className="flex flex-wrap gap-2 mb-6">
                <div className={`${getScoreBadgeColor(selectedArticle.overall_score)} px-4 py-2 rounded-xl font-bold shadow-md`}>
                  📊 Score: {selectedArticle.overall_score}
                </div>
                {selectedArticle.applicable_exams?.map(exam => (
                  <span key={exam} className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md">
                    {exam}
                  </span>
                ))}
              </div>

              {/* Summary */}
              {selectedArticle.crisp_summary && (
                <div className="mb-6 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-100">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                    <span className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg p-2 mr-3">
                      <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </span>
                    Summary
                  </h4>
                  <p className="text-gray-700 leading-relaxed">{selectedArticle.crisp_summary}</p>
                </div>
              )}

              {/* Key Bullets */}
              {selectedArticle.key_bullets && selectedArticle.key_bullets.length > 0 && (
                <div className="mb-6 p-6 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl border border-amber-100">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                    <span className="bg-gradient-to-r from-amber-600 to-yellow-600 rounded-lg p-2 mr-3">
                      <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </span>
                    Key Points
                  </h4>
                  <ul className="space-y-2">
                    {selectedArticle.key_bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="inline-block w-2 h-2 bg-amber-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span className="text-gray-700 leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Article Content */}
              <div className="prose max-w-none">
                <div className="text-gray-700 whitespace-pre-wrap leading-relaxed text-justify">
                  {selectedArticle.cleaned_content || selectedArticle.content}
                </div>
              </div>

              {/* Key Topics */}
              {selectedArticle.key_topics && selectedArticle.key_topics.length > 0 && (
                <div className="mt-8 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                    <span className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-2 mr-3">
                      <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </span>
                    Key Topics
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedArticle.key_topics.map((topic, idx) => (
                      <span key={idx} className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-white text-gray-800 shadow-sm border border-gray-200">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Reasoning */}
              {selectedArticle.reasoning && (
                <div className="mt-6 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                    <span className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg p-2 mr-3">
                      <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </span>
                    AI Reasoning
                  </h4>
                  <p className="text-gray-700 leading-relaxed">{selectedArticle.reasoning}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
